import { useState, useEffect, useCallback, useRef } from "react";
import { accountsService } from "services";
import { transactionsService } from "services";
import { plansService } from "services";

/**
 * Hook para obtener horas objetivo y planificadas para el balance.
 *
 * Expone `fetchForEmployees(ids)` — un callback estable que useAttendanceData
 * llama con los mismos employee IDs que ya calculó para alertas.
 *
 * Los IDs que llegan mientras `enabled` es false se guardan en `pendingIdsRef`.
 * Cuando `enabled` pasa a true, se fetchean automáticamente.
 */
export default function useObjectivePlannedHours(
	filterParams,
	{ enabled = true } = {}
) {
	const [objectiveHours, setObjectiveHours] = useState([]);
	const [plannedHours, setPlannedHours] = useState([]);

	const enabledRef = useRef(enabled);
	enabledRef.current = enabled;

	const filterParamsRef = useRef(filterParams);
	filterParamsRef.current = filterParams;

	const accountObjetivoRef = useRef(null);
	const loadedEmployeeIdsRef = useRef(new Set());
	const pendingIdsRef = useRef(new Set());

	// Cargar cuenta objetivo una vez
	useEffect(() => {
		if (!accountObjetivoRef.current) {
			accountsService.getAll("Horas objetivo").then((response) => {
				if (response?.data) {
					accountObjetivoRef.current = response;
				}
			});
		}
	}, []);

	// Resetear cuando cambian los filtros
	useEffect(() => {
		setObjectiveHours([]);
		setPlannedHours([]);
		loadedEmployeeIdsRef.current = new Set();
		pendingIdsRef.current = new Set();
	}, [filterParams?.startDate, filterParams?.endDate, filterParams?.employeeIds]);

	const doFetch = useCallback((ids) => {
		const fp = filterParamsRef.current;
		if (!fp?.startDate || !fp?.endDate) return;
		if (!accountObjetivoRef.current) return;

		ids.forEach((id) => loadedEmployeeIdsRef.current.add(id));

		const idsStr = ids.join(",");
		const accountObjetivoId =
			accountObjetivoRef.current?.data?.length > 0
				? accountObjetivoRef.current.data[0].id
				: -1;

		Promise.all([
			transactionsService.getAll(
				idsStr,
				accountObjetivoId,
				fp.startDate,
				fp.endDate,
				"",
				0,
				"+day"
			),
			plansService.getPlannedHours({
				employee_ids: idsStr,
				start: fp.startDate,
				end: fp.endDate,
			}),
		])
			.then(([responseObjective, responsePlanned]) => {
				if (responseObjective?.data) {
					setObjectiveHours((prev) => [...prev, ...responseObjective.data]);
				}
				if (Array.isArray(responsePlanned)) {
					setPlannedHours((prev) => [...prev, ...responsePlanned]);
				}
			})
			.catch((error) => {
				console.error("Error fetching planned hours:", error);
			});
	}, []);

	// Callback estable (deps vacías) — lee todo desde refs
	const fetchForEmployees = useCallback((employeeIds) => {
		const newIds = employeeIds.filter(
			(id) => !loadedEmployeeIdsRef.current.has(id) && !pendingIdsRef.current.has(id)
		);
		if (newIds.length === 0) return;

		if (!enabledRef.current) {
			newIds.forEach((id) => pendingIdsRef.current.add(id));
			return;
		}

		doFetch(newIds);
	}, [doFetch]);

	// Cuando enabled pasa a true, fetchear todos los IDs pendientes
	useEffect(() => {
		if (enabled && pendingIdsRef.current.size > 0) {
			const pending = [...pendingIdsRef.current];
			pendingIdsRef.current = new Set();
			doFetch(pending);
		}
	}, [enabled, doFetch]);

	return {
		objectiveHours,
		plannedHours,
		fetchForEmployees,
	};
}
